import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const faqData = [
  {
    category: "Paint Protection Film (PPF)",
    questions: [
      {
        question: "Is Paint Protection Film (PPF) worth the cost?",
        answer: "PPF is a significant investment in protecting your vehicle's value, especially for high-end, luxury, and enthusiast cars. Its primary benefit is protecting the original paint from rock chips, scratches, and environmental contaminants that are common on Las Vegas roads. For owners who want to maintain a pristine finish and maximize resale value, it is absolutely worth the investment. The cost of repainting damaged panels often exceeds the cost of preventative PPF installation."
      },
      {
        question: "How much does PPF cost in Las Vegas?",
        answer: "The cost of PPF is not one-size-fits-all, as it depends on several key factors: 1) The vehicle's size and complexity (a truck has more surface area than a coupe), 2) The brand of film (we use premium XPEL), and 3) The coverage package you choose (e.g., Full Front, Track Pack, or Full Vehicle). Because every car and owner's needs are different, we provide a custom quote. Please contact us for a free, accurate estimate based on your specific vehicle."
      },
      {
        question: "How much does a \"Full Body\" PPF wrap cost?",
        answer: "A full body wrap is the ultimate in protection and is custom-quoted for every vehicle. The final cost depends on the vehicle's size (e.g., a sedan vs. a large SUV) and the complexity of the body panels. Vehicles with more intricate curves, larger surface areas, or special trim require additional material and installation time. For a precise and competitive quote for your specific car, please contact our team for a free consultation."
      },
      {
        question: "How long does PPF last?",
        answer: "High-quality, professionally installed films like XPEL are warrantied for 10 years. This warranty covers yellowing, cracking, and peeling. The actual lifespan depends on maintenance and exposure to the elements, but with proper care, XPEL PPF can maintain its protective properties and appearance for the full decade. In the harsh Las Vegas climate, this durability is particularly valuable."
      },
      {
        question: "What are the disadvantages of PPF?",
        answer: "The main disadvantage is the upfront cost, which can be significant for full vehicle coverage. Additionally, poor-quality film or improper installation can lead to peeling, bubbling, or a poor finish. However, these issues are avoided by using certified installers and premium brands like XPEL. At Battle Born Protection Films, we use only the best materials and certified installation techniques to ensure you avoid these problems entirely."
      },
      {
        question: "Can you wax or pressure wash a car with PPF?",
        answer: "Yes, you can wax a car with PPF, but a ceramic coating applied over the film is recommended for even better results and easier maintenance. For pressure washing, it's completely safe as long as you stay at least 12-18 inches away from the film's edges to prevent lifting. Avoid directing high-pressure water at seams or edges at close range."
      }
    ]
  },
  {
    category: "Ceramic Coatings",
    questions: [
      {
        question: "How much does a ceramic coating cost in Las Vegas?",
        answer: "The price for a ceramic coating is determined by two main factors: 1) The condition of your paint (which dictates the level of paint correction needed before application) and 2) The duration of the coating you choose (e.g., 1-year, 4-year, or 8-year packages). Heavily scratched or swirled paint requires more prep work. We inspect every vehicle to provide a fair and accurate quote. Please contact us to schedule a free consultation and estimate."
      },
      {
        question: "Is a ceramic coating worth the cost?",
        answer: "Absolutely. Ceramic coatings provide multiple benefits: a deep, lasting gloss that makes your vehicle look showroom-new; UV protection (critical in the intense Nevada sun) that prevents paint fading; and extreme hydrophobic properties that cause water to bead off, making the car much easier to clean. It's a long-term investment in paint preservation and ease of maintenance that pays dividends over the life of your vehicle."
      },
      {
        question: "How long does a ceramic coating last?",
        answer: "This depends on the package you choose. We offer professional-grade coatings that last 1-year, 4-years, or 8+ years with proper maintenance. This is far more durable than any consumer-grade product you can buy at an auto parts store. The longevity also depends on how well you maintain the coating with regular washing and proper care techniques."
      },
      {
        question: "Which is better, 9H or 10H ceramic coating?",
        answer: "The 'H' refers to the pencil hardness scale used to measure coating durability. 9H is the industry-standard for high-quality, durable ceramic coatings and provides exceptional, proven protection. The term '10H' is often a marketing term, as 9H is the highest rating on the standardized pencil hardness scale. A properly installed 9H coating from a reputable brand provides all the protection your paint needs."
      },
      {
        question: "What is the downside of ceramic coating?",
        answer: "The main downsides are the upfront cost and the fact that ceramic coatings do not protect against rock chips or deep scratches (unlike PPF). Additionally, ceramic coatings still require maintenance—regular washing is essential for the coating to perform its best. It's not 'set it and forget it' protection. However, the washing process is much easier and faster than on uncoated paint due to the hydrophobic properties."
      },
      {
        question: "How do I maintain a ceramic coated car? Can I take it through a carwash?",
        answer: "The best way to maintain a ceramic coated car is with a 2-bucket hand wash using pH-neutral soap. Touchless car washes are generally safe, but you should absolutely avoid automatic brush washes, as the brushes can instill micro-scratches and potentially damage the coating over time. Regular hand washing every 1-2 weeks will keep your coating performing optimally."
      }
    ]
  },
  {
    category: "Window Tint",
    questions: [
      {
        question: "What is the darkest legal tint in Las Vegas?",
        answer: "In Nevada, the front side windows must allow more than 35% of light through (35% VLT minimum). The rear side windows and rear windshield can be tinted as dark as you want, including 5% 'limo tint'. The front windshield can only have a non-reflective tint strip on the top 5 inches. We ensure all our installations comply with Nevada state law."
      },
      {
        question: "How much does window tint cost in Las Vegas?",
        answer: "The cost of window tinting depends on two key factors: 1) The type of film you select (standard dyed film vs. high-performance ceramic tint, which offers superior heat and UV rejection—critical in Las Vegas), and 2) The number of windows being tinted (a coupe has fewer windows than a large SUV). We're happy to walk you through the options and explain the benefits of each film type. Please contact us for a free estimate tailored to your vehicle."
      },
      {
        question: "What is the difference between 5%, 20%, and 35% tint? Which is darker?",
        answer: "The percentage refers to 'Visible Light Transmission' (VLT), or how much light the tint lets in. This means a lower number is darker. 5% ('limo tint') is the darkest and lets in only 5% of light, 20% is dark but offers some visibility from outside, and 35% is the lightest of the three and is the legal limit for front windows in Nevada. Lower percentage = darker tint."
      },
      {
        question: "Is it hard to see out of 35% tint at night?",
        answer: "No. 35% tint provides a noticeable reduction in glare and heat but is considered very safe and easy to see out of at night. This is why it's the legal limit for front windows in Nevada and many other states. It strikes the perfect balance between style, comfort, and visibility."
      },
      {
        question: "Is it hard to see out of 20% tint at night?",
        answer: "It can be. 20% tint is significantly darker and can reduce visibility at night, especially in poorly lit areas or when backing up. We recommend 20% tint for rear windows where outward visibility is less critical, but advise clients to consider 35% for front side windows if night visibility is a major concern for safety."
      }
    ]
  }
];

const FAQSection = () => {
  return (
    <section className="py-20 bg-gradient-to-b from-background to-darker-bg">
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="text-center mb-12 animate-fade-in">
          <h2 className="text-4xl md:text-5xl font-heading font-bold mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-lg text-muted-foreground">
            Authoritative answers to your questions about vehicle protection in Las Vegas
          </p>
        </div>

        <div className="space-y-8">
          {faqData.map((category, categoryIndex) => (
            <div key={categoryIndex} className="space-y-4">
              <h3 className="text-2xl font-heading font-bold text-accent">
                {category.category}
              </h3>
              <Accordion type="single" collapsible className="w-full space-y-2">
                {category.questions.map((item, questionIndex) => (
                  <AccordionItem
                    key={questionIndex}
                    value={`${categoryIndex}-${questionIndex}`}
                    className="bg-card border border-border rounded-lg px-6 hover:border-accent transition-colors"
                  >
                    <AccordionTrigger className="text-left hover:no-underline py-6">
                      <span className="font-semibold text-foreground pr-4">
                        {item.question}
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground pb-6 leading-relaxed">
                      {item.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
export { faqData };
